import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

export type DashboardSection =
  | "overview"
  | "profile"
  | "portfolio"
  | "experience"
  | "notifications"
  | "settings";

// Date range type for analytics
export type DateRange = { from: Date | undefined; to?: Date | undefined };

interface DashboardContextType {
  activeSection: DashboardSection;
  setActiveSection: (section: DashboardSection) => void;
  navigateToSection: (section: DashboardSection) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined
);

interface DashboardProviderProps {
  children: ReactNode;
}

// Helper functions for default date ranges
const getLast30DaysRange = (): DateRange => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { from, to };
};

const getLast7DaysRange = (): DateRange => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return { from, to };
};

export function DashboardProvider({ children }: DashboardProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Get section from current URL path
  const getSectionFromPath = (): DashboardSection => {
    const validSections: DashboardSection[] = [
      "overview",
      "profile",
      "portfolio",
      "experience",
      "notifications",
      "settings",
    ];

    const pathSegments = location.pathname.split("/");
    const section = pathSegments[pathSegments.length - 1];

    if (section && validSections.includes(section as DashboardSection)) {
      return section as DashboardSection;
    }

    return "overview";
  };

  const [activeSection, setActiveSection] = useState<DashboardSection>(
    getSectionFromPath()
  );

  // Date range state with responsive default
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Initialize with mobile-friendly range, will be updated on mount
    return getLast7DaysRange();
  });

  // Update active section when URL changes
  useEffect(() => {
    const newSection = getSectionFromPath();
    setActiveSection(newSection);
  }, [location.pathname]);

  // Responsive date range initialization
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      
      // Update date range based on screen size if it's still the default
      if (mobile && dateRange.from && dateRange.to) {
        const isDefault7Days = dateRange.from.getTime() === getLast7DaysRange().from?.getTime();
        if (isDefault7Days) {
          setDateRange(getLast7DaysRange());
        }
      } else if (!mobile && dateRange.from && dateRange.to) {
        const isDefault7Days = dateRange.from.getTime() === getLast7DaysRange().from?.getTime();
        if (isDefault7Days) {
          setDateRange(getLast30DaysRange());
        }
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [dateRange]);

  const handleSetActiveSection = (newSection: DashboardSection) => {
    setActiveSection(newSection);
  };

  const navigateToSection = (newSection: DashboardSection) => {
    setActiveSection(newSection);
    navigate(`/dashboard/${newSection}`);
  };

  const value: DashboardContextType = {
    activeSection,
    setActiveSection: handleSetActiveSection,
    navigateToSection,
    dateRange,
    setDateRange,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboardContext() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider"
    );
  }
  return context;
}

// Convenience hook for accessing just the date range
export function useDashboardDateRange() {
  const { dateRange, setDateRange } = useDashboardContext();
  return { dateRange, setDateRange };
}
